import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * Card. House rules:
 *   - rounded-md (6px), 1px slate-200 border, navy-tinted shadow-soft.
 *   - Optional `hover` lift via shadow-lift on group-hover. Use for clickable cards only.
 *   - Tight inner padding (p-4 / p-5). No marketing-card whitespace.
 *   - Never 2xl-rounded. Never gray drop shadows.
 */
interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Add hover-lift. Use only when the whole card is interactive. */
  hover?: boolean
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, hover, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-md border border-slate-200 bg-card text-card-foreground shadow-soft',
        hover && 'transition-shadow duration-fluid ease-fluid hover:shadow-lift motion-reduce:transition-none',
        className,
      )}
      {...props}
    />
  ),
)
Card.displayName = 'Card'

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col space-y-1 px-5 pt-4 pb-3', className)} {...props} />
  ),
)
CardHeader.displayName = 'CardHeader'

const CardTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('text-md font-semibold leading-tight tracking-tight text-navy-900', className)}
      {...props}
    />
  ),
)
CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('text-xs text-muted-foreground', className)} {...props} />
  ),
)
CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('px-5 pb-4', className)} {...props} />
  ),
)
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center justify-between border-t border-slate-200 px-5 py-3', className)} {...props} />
  ),
)
CardFooter.displayName = 'CardFooter'

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
